import React, { useState, useEffect } from 'react';
import { Plus, UserCheck, Building2, Search, X } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { CreateDonorData, Office } from '../../lib/sdk/index';
import { toast } from 'react-toastify';
import { useOffices, useCreateDonor } from '../../hooks/useQueries';
import { printDonorRegistrationSlip } from '../../lib/printDonorSlip';
import {
  formatDonorApiError,
  normalizeOptionalEmail,
  normalizeOptionalPhone,
  normalizeOptionalText,
  summarizeFieldErrors,
} from '../../lib/donorFormUtils';

const AddDonors: React.FC = () => {
  // Use React Query hooks
  const { data: officesResponse, isLoading: officesLoading } = useOffices();
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : (Array.isArray(officesResponse) ? officesResponse : []);
  
  const [showOfficeDropdown, setShowOfficeDropdown] = useState(false);
  const [officeSearchTerm, setOfficeSearchTerm] = useState('');
  const [formData, setFormData] = useState<CreateDonorData>({
    name: '',
    fatherOrHusbandName: '',
    relation: 'Father',
    cnic: '',
    phone: '',
    email: '',
    address: '',
    officeId: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Create donor mutation
  const createDonorMutation = useCreateDonor({
    onSuccess: async (data: any) => {
      setFormData({
        name: '',
        fatherOrHusbandName: '',
        relation: 'Father',
        cnic: '',
        phone: '',
        email: '',
        address: '',
        officeId: ''
      });
      setOfficeSearchTerm('');
      setErrors({});
      // Print a registration slip with the assigned Donor ID (desktop only).
      await printDonorRegistrationSlip(data, 'donor');
    },
    onError: (error: unknown) => {
      console.error('Error creating donor:', error);
      toast.error(formatDonorApiError(error, 'Failed to create donor. Please try again.'));
    }
  });

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOfficeDropdown) {
        setShowOfficeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOfficeDropdown]);


  // Auto-format CNIC input
  const formatCNIC = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 12) return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(12, 13)}`;
  };

  // Auto-format phone input
  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    
    // If user starts typing without +92, automatically add it
    if (!value.startsWith('+92')) {
      // Always add +92- prefix for new phone numbers
      if (cleaned.length <= 10) {
        return `+92-${cleaned}`;
      }
      return `+92-${cleaned.slice(0, 10)}`;
    }
    
    // If already has +92, just format the rest
    const withoutPrefix = cleaned.slice(2); // Remove 92
    if (withoutPrefix.length <= 0) return '+92-';
    if (withoutPrefix.length <= 10) return `+92-${withoutPrefix}`;
    return `+92-${withoutPrefix.slice(0, 10)}`;
  };

  const handleCNICChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNIC(e.target.value);
    setFormData({ ...formData, cnic: formatted });
    if (errors.cnic) setErrors({ ...errors, cnic: '' });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
    if (errors.phone) setErrors({ ...errors, phone: '' });
  };

  const validateForm = (): { valid: boolean; fieldErrors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Donor name is required';
    }

    if (!formData.fatherOrHusbandName.trim()) {
      newErrors.fatherOrHusbandName = 'Father/Husband name is required';
    }

    if (!formData.cnic.trim()) {
      newErrors.cnic = 'CNIC is required';
    } else if (!/^\d{5}-\d{7}-\d$/.test(formData.cnic)) {
      newErrors.cnic = 'CNIC must be in format XXXXX-XXXXXXX-X';
    }

    const phone = normalizeOptionalPhone(formData.phone);
    if (phone && !/^\+92-\d{10}$/.test(phone)) {
      newErrors.phone = 'If you enter a phone number, use format +92-XXXXXXXXXX (10 digits after +92-)';
    }

    const email = normalizeOptionalEmail(formData.email);
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address, or leave email blank';
    }

    if (!formData.officeId.trim()) {
      newErrors.officeId = 'Office is required — search and select an office from the list';
    }

    setErrors(newErrors);
    return { valid: Object.keys(newErrors).length === 0, fieldErrors: newErrors };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard against rapid double-submits (double-click / slow network): the
    // mutation's in-flight state is the single source of truth, so a second
    // click while the first request is pending is ignored.
    if (createDonorMutation.isLoading) {
      return;
    }

    const { valid, fieldErrors } = validateForm();
    if (!valid) {
      toast.error(summarizeFieldErrors(fieldErrors));
      return;
    }

    createDonorMutation.mutate({
      ...formData,
      phone: normalizeOptionalPhone(formData.phone),
      email: normalizeOptionalEmail(formData.email),
      address: normalizeOptionalText(formData.address),
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      fatherOrHusbandName: '',
      relation: 'Father',
      cnic: '',
      phone: '',
      email: '',
      address: '',
      officeId: ''
    });
    setOfficeSearchTerm('');
    setErrors({});
  };

  const filteredOffices = offices.filter(office =>
    office.name.toLowerCase().includes(officeSearchTerm.toLowerCase()) ||
    office.officeNo.toString().includes(officeSearchTerm)
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Add New Donor</h1>
        <p className="text-gray-600 dark:text-gray-400">Create a new permanent donor with complete information</p>
      </div>

      {/* Information Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <UserCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Donor Information</h3>
                         <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
               Permanent donors require a valid CNIC and are assigned to a specific office. 
               All fields marked with * are required. The donor will receive an auto-incremented donor ID (e.g., D001, D002) upon creation.
             </p>
          </div>
        </div>
      </div>
      
      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Donor Name *</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                placeholder="Enter donor's full name"
                className={errors.name ? 'border-red-500' : ''}
                required
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="form-label">Father/Husband Name *</label>
              <Input
                type="text"
                value={formData.fatherOrHusbandName}
                onChange={(e) => {
                  setFormData({ ...formData, fatherOrHusbandName: e.target.value });
                  if (errors.fatherOrHusbandName) setErrors({ ...errors, fatherOrHusbandName: '' });
                }}
                placeholder="Enter father or husband name"
                className={errors.fatherOrHusbandName ? 'border-red-500' : ''}
                required
              />
              {errors.fatherOrHusbandName && <p className="text-red-500 text-xs mt-1">{errors.fatherOrHusbandName}</p>}
            </div>

            <div>
              <label className="form-label">Relation *</label>
              <select
                value={formData.relation}
                onChange={(e) => setFormData({ ...formData, relation: e.target.value as 'Father' | 'Husband' })}
                className="form-select"
                required
              >
                <option value="Father">Father</option>
                <option value="Husband">Husband</option>
              </select>
            </div>

            <div>
              <label className="form-label">CNIC *</label>
              <Input
                type="text"
                value={formData.cnic}
                onChange={handleCNICChange}
                placeholder="XXXXX-XXXXXXX-X"
                maxLength={15}
                className={errors.cnic ? 'border-red-500' : ''}
                required
              />
              {errors.cnic && <p className="text-red-500 text-xs mt-1">{errors.cnic}</p>}
            </div>

                         <div>
               <label className="form-label">Phone Number</label>
               <Input
                 type="text"
                 value={formData.phone}
                 onChange={handlePhoneChange}
                 placeholder="Add number without 0, e.g. 3331112223"
                 maxLength={14}
                 className={errors.phone ? 'border-red-500' : ''}
               />
               <p className="text-xs text-gray-500 mt-1">Automatically formats to +92-XXXXXXXXXX format</p>
               {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
             </div>

            <div>
              <label className="form-label">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                placeholder="Enter email address"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="form-label">Address</label>
              <Input
                type="text"
                value={formData.address || ''}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                }}
                placeholder="Enter address (optional)"
                className=""
              />
            </div>
            
            <div className="md:col-span-1">
              <label className="form-label">Office *</label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search offices..."
                  value={officeSearchTerm}
                  onChange={(e) => {
                    setOfficeSearchTerm(e.target.value);
                    setShowOfficeDropdown(true);
                  }}
                  onFocus={() => setShowOfficeDropdown(true)}
                  className={errors.officeId ? 'border-red-500' : ''}
                  required
                />
                
                {/* Office dropdown */}
                {showOfficeDropdown && (
                  <div className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                    {filteredOffices.map((office) => (
                      <div
                        key={office._id}
                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          setFormData({ ...formData, officeId: office._id });
                          setOfficeSearchTerm(`${office.name} (Office #${office.officeNo})`);
                          setShowOfficeDropdown(false);
                          if (errors.officeId) setErrors({ ...errors, officeId: '' });
                        }}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {office.name} - Office #{office.officeNo}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {office.isCentral ? 'Central Office' : 'Regional Office'}
                        </div>
                      </div>
                    ))}
                    {filteredOffices.length === 0 && (
                      <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {officeSearchTerm ? 'No offices found' : 'Type to search offices...'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Current selection display */}
              {formData.officeId && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Selected Office: <span className="font-medium text-gray-900 dark:text-gray-100">
                    {offices.find(o => o._id === formData.officeId)?.name || 'Unknown'}
                  </span>
                </div>
              )}
              
              {errors.officeId && <p className="text-red-500 text-xs mt-1">{errors.officeId}</p>}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex items-center gap-2" disabled={createDonorMutation.isLoading}>
              {createDonorMutation.isLoading ? <LoadingSpinner size="sm" /> : <Plus className="h-4 w-4" />}
              {createDonorMutation.isLoading ? 'Creating...' : 'Create Donor'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm} className="flex items-center gap-2">
              <X className="h-4 w-4" />
              Reset Form
            </Button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default AddDonors;
